import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import {
  SingleSessionProviderContext,
  ProctoringContext,
  useServices,
  useSnackbar,
  useLoading,
} from "contexts";
import { useStackSnackbar } from "contexts/StackSnackbarProvider";
import { useNavigateWithClient } from "hooks";
import {
  analyseAnswer,
  finishInterviewAttempt,
  getRandomQuestionsByTemplateId,
  getTemplateById,
  saveInterviewAttempt,
  skipQuestion,
} from "services";
import { Session, round } from "utils";
import WavRecorder from "utils/WavRecorder";
import useAssessmentStore from "components/Assessment/assessmentStore";
import {
  AssessmentStates,
  QuestionStates,
} from "components/Assessment/constants";
import { templateTypes } from "components/PerformanceReview/constants";

import MobileAppShell from "../../layout/MobileAppShell";
import FullscreenDialog from "./FullscreenDialog";
import TotalTimer from "./TotalTimer";
import EndSession from "./EndSession";
import QuestionCard from "./QuestionCard";
import QuestionPills from "./QuestionPills";
import PostAssessment from "./PostAssessment";

// Mobile ships practice + assignment only; both fetch questions the same way.
const SUPPORTED_TYPES = [templateTypes.PRACTICE, templateTypes.ASSIGNMENT];

/**
 * MobileInterview — the mobile assessment screen (`/:client/assessment?tid=…`).
 *
 * Mirrors desktop `components/Assessment/index.jsx`: it drives the same shared
 * `assessmentStore` state machine and calls the same services, so the states,
 * timers, proctoring and edge cases are identical. Only the UI is mobile's own.
 *
 * Two deliberate differences from desktop:
 *  - No tutorial. Desktop gates it on localStorage; mobile always skips straight
 *    to the fullscreen gate, and never touches that key so desktop's own
 *    first-run tutorial is unaffected.
 *  - Only practice/assignment templates. Anything else is refused up front
 *    rather than half-working (personalized needs the interviewer/TTS UI).
 */
export default function MobileInterview() {
  const [searchParams] = useSearchParams();
  const snackbar = useSnackbar();
  const service = useServices();
  const loading = useLoading();
  const navigate = useNavigateWithClient();
  const { showStackSnackbar, closeStackSnackbar } = useStackSnackbar();
  const { _showDuplicateSessionDialog } = React.useContext(
    SingleSessionProviderContext
  );

  const hasIframe = React.useMemo(() => window.top !== window.self, []);

  const wavRecorder = React.useRef(null);
  const questionTimeTakenRef = React.useRef(0);
  const resultCheckerIntervalRef = React.useRef(null);

  const {
    endProctoring,
    getProctoringData,
    activateFullScreenProctoring,
    activateGeneralProctoring,
    enableFullscreenMode,
    fullScreenActive,
  } = React.useContext(ProctoringContext);

  const template = useAssessmentStore((s) => s.template);
  const attempt = useAssessmentStore((s) => s.attempt);
  const questions = useAssessmentStore((s) => s.questions);
  const currentQuestion = useAssessmentStore((s) => s.currentQuestion);
  const totalTime = useAssessmentStore((s) => s.totalTime);
  const remainingInterviewTime = useAssessmentStore(
    (s) => s.remainingInterviewTime
  );
  const isTimeout = useAssessmentStore((s) => s.isTimeout);
  const assessmentState = useAssessmentStore((s) => s.assessmentState);
  const questionState = useAssessmentStore((s) => s.questionState);

  const setTemplate = useAssessmentStore((s) => s.setTemplate);
  const setTemplateId = useAssessmentStore((s) => s.setTemplateId);
  const setQuestions = useAssessmentStore((s) => s.setQuestions);
  const setAttempt = useAssessmentStore((s) => s.setAttempt);
  const setAssessmentState = useAssessmentStore((s) => s.setAssessmentState);
  const setQuestionState = useAssessmentStore((s) => s.setQuestionState);
  const setCurrentQuestion = useAssessmentStore((s) => s.setCurrentQuestion);
  const setError = useAssessmentStore((s) => s.setQuestionError);
  const setUploadState = useAssessmentStore((s) => s.setUploadState);
  const setUploadProgress = useAssessmentStore((s) => s.setUploadProgress);
  const reset = useAssessmentStore((s) => s.reset);
  const start = useAssessmentStore((s) => s.start);
  const next = useAssessmentStore((s) => s.next);
  const startAssessment = useAssessmentStore((s) => s.startAssessment);
  const endTutorial = useAssessmentStore((s) => s.endTutorial);
  const fetchQuestion = useAssessmentStore((s) => s.fetchQuestion);

  const [openFullscreenDialog, setOpenFullscreenDialog] = React.useState(false);

  const question = React.useMemo(
    () => questions[currentQuestion] || {},
    [questions, currentQuestion]
  );

  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the template, then turn on the proctoring it asks for.
  React.useEffect(() => {
    (async () => {
      try {
        const tid = searchParams.get("tid");
        const loaded = await getTemplateById(tid, { module: true });

        if (!SUPPORTED_TYPES.includes(loaded?.type)) {
          snackbar.warn(
            "This assessment isn't available on mobile yet. Please use a desktop browser."
          );
          navigate("/home");
          return;
        }

        activateFullScreenProctoring();
        if (loaded?.proctorSettings?.generalProctoring) {
          activateGeneralProctoring();
        }

        setTemplate(loaded);
      } catch (error) {
        console.error(error);
        snackbar.error("Uh Oh! Unable to start the mock. Please try again!");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block the back button for the duration of the attempt, and tear proctoring
  // down on the way out — same as desktop.
  React.useEffect(() => {
    const listener = () => window.history.go(1);
    window.addEventListener("popstate", listener);

    return () => {
      window.removeEventListener("popstate", listener);
      endProctoring();
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (assessmentState === AssessmentStates.FULLSCREEN_PERMISSION) {
      setOpenFullscreenDialog(true);
    }
  }, [assessmentState]);

  const handleSuccessFullscreenMode = () => {
    enableFullscreenMode();
    setOpenFullscreenDialog(false);
    if (assessmentState === AssessmentStates.FULLSCREEN_PERMISSION) {
      fetchQuestion();
    }
  };

  const handleDialogClosed = () => {
    Session.clearSession();
    navigate("/login");
  };

  // FETCHING_QUESTIONS — desktop does this inside QuestionSection.
  React.useEffect(() => {
    if (assessmentState !== AssessmentStates.FETCHING_QUESTIONS) return;
    if (!template?.type) return;

    (async () => {
      const templateId = searchParams.get("tid");

      if (!templateId) {
        navigate("/home");
        snackbar.warn("Please, Select a template to take an Interview!!");
        return;
      }

      try {
        loading.show();

        // `interviewTime` is what the API packs questions to fill, so it has to
        // be the template's own testTime. Left to its default (1800s) a longer
        // template silently comes back with only the questions that fit 30
        // minutes — and `totalTime` is then derived from that short list, so the
        // timer disagrees with the template too.
        const { interviewQuestions } = await service.callService(
          getRandomQuestionsByTemplateId,
          templateId,
          template?.metadata?.testTime || 1800
        );

        if (!interviewQuestions?.length) {
          snackbar.error(
            "There are no questions in this template, Please add some questions."
          );
          navigate("/home");
          return;
        }

        setTemplateId(templateId);
        setQuestions(interviewQuestions);
        setAssessmentState(AssessmentStates.STARTING);
      } catch (error) {
        console.error(error);
        snackbar.error("Unable to begin assessment, Please try again!!");
        navigate("/home");
      } finally {
        loading.hide();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState, template]);

  const startInterview = React.useCallback(async () => {
    try {
      const templateId = searchParams.get("tid");

      if (attempt._id) return;

      const attemptResult = await service.callService(
        saveInterviewAttempt,
        null,
        {
          interviewTemplate: templateId,
          startedAt: new Date().toISOString(),
          totalQuestions: questions.length,
          pathway: template.pathway,
          batch: sessionStorage.getItem("selectedBatch"),
        }
      );
      setAttempt(attemptResult);
      startAssessment();

      window?.parent?.postMessage(
        { event: "ASSESSMENT_STARTED", attemptId: attemptResult._id },
        "*"
      );
    } catch (error) {
      console.error(error);
      endProctoring();
      snackbar.error(
        error.message || "Uh Oh! Unable to start the mock. Please try again!"
      );
      navigate("/home");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, questions, searchParams, template]);

  const finishInterview = React.useCallback(async () => {
    if (!attempt?.finishedAt) {
      try {
        const proctoringRecords = getProctoringData();

        const { isActive } = await service.callService(
          finishInterviewAttempt,
          attempt._id,
          isTimeout,
          proctoringRecords
        );

        if (isActive) {
          setAttempt({ ...attempt, finishedAt: new Date() });
          setAssessmentState(AssessmentStates.COMPLETED);
          window?.parent?.postMessage(
            { event: "ASSESSMENT_FINISHED", attemptId: attempt._id },
            "*"
          );
        } else {
          reset();
          snackbar.info("Attempt not considered, none of the answers recorded!");
          wavRecorder.current?.stop();

          if (Session.magicLinkRedirectUrl) {
            window.open(Session.magicLinkRedirectUrl, "_top");
          } else {
            navigate("/home");
          }
        }
      } catch (error) {
        console.error(error);
        // Desktop retries indefinitely rather than lose the attempt.
        setTimeout(finishInterview, 3000);
      }
    } else {
      navigate("/home");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, attempt, isTimeout]);

  const checkResults = React.useCallback(() => {
    if (questionState === "RECORDING") {
      setQuestionState(QuestionStates.SUBMITTED);
      return false;
    }

    for (const i in questions) {
      const q = questions[i] || {};
      const { submitted, recorded, visited } = q;
      if (recorded && !submitted) return false;

      // Anything never answered gets explicitly skipped server-side.
      if (!visited || (visited && !submitted)) {
        q.visited = true;
        q.submitted = true;
        service
          .callService(skipQuestion, {
            questionId: q._id,
            attemptId: attempt._id,
            timeTaken: 0,
            transcript: "",
            serialNumber: i,
          })
          .catch(console.error)
          .finally(() => {
            q.analysisRecieved = true;
          });
      }
    }

    for (const q of questions) {
      const { analysisRecieved, submitted } = q;
      if (submitted && !analysisRecieved) return false;
    }

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionState, attempt, questions]);

  const handleAudioSuccess = React.useCallback(
    async (audioBlob) => {
      setQuestionState(QuestionStates.WAITING);

      const questionId = question._id;

      question.analysisData = audioBlob;
      question.recorded = true;

      const cQ = currentQuestion;

      const analysisData = {
        attemptId: attempt._id,
        questionId,
        timeTaken: question.timeTaken || 0,
        serialNumber: cQ,
        audioBlob,
        template: template._id,
        domain: template.domain?._id ? template.domain?._id : template.domain,
      };

      try {
        const handleEvent = (event, data) => {
          setUploadState(cQ, event === "progress");

          if (data?.name === "DuplicateSession") {
            _showDuplicateSessionDialog(
              `Already logged in on ${data.browser}, ${data.os}.
              Do you wish to login here?`,
              async () => {
                handleDialogClosed();
              }
            );
          }

          if (event === "progress") {
            setUploadProgress(cQ, data);
          } else {
            setError(cQ, event === "error" || data?.analysis?.isError);

            if (event === "error") {
              showStackSnackbar(`Q${cQ + 1} Not Recorded Properly:`, {
                variant: "error",
                persist: true,
                action: (id) => (
                  <RetakeButton
                    onClick={() => {
                      closeStackSnackbar(id);
                      setCurrentQuestion(cQ);
                      start();
                    }}
                  />
                ),
              });
            }
          }

          question.analysisRecieved = event !== "progress";
        };

        analyseAnswer(analysisData, handleEvent);
      } catch (error) {
        setError(cQ, true);
        console.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question, attempt, currentQuestion, template]
  );

  // The assessment state machine.
  React.useEffect(() => {
    switch (assessmentState) {
      case AssessmentStates.LOADED:
      case AssessmentStates.SHOWING_TUTORIAL:
        // Mobile has no tutorial — go straight to the fullscreen gate.
        endTutorial();
        break;

      case AssessmentStates.STARTING:
        startInterview();
        break;

      case AssessmentStates.FINISHING:
        endProctoring();
        finishInterview();
        break;

      case AssessmentStates.COMPLETED:
        wavRecorder.current?.stop();
        wavRecorder.current = null;
        break;

      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState]);

  // Poll until every answer has finished uploading/analysing.
  React.useEffect(() => {
    if (assessmentState === AssessmentStates.WAITING_FOR_RESULT) {
      resultCheckerIntervalRef.current = setInterval(() => {
        if (checkResults()) {
          clearInterval(resultCheckerIntervalRef.current);
          setAssessmentState(AssessmentStates.FINISHING);
        }
      }, 1000);
    }

    return () => {
      if (resultCheckerIntervalRef.current) {
        clearInterval(resultCheckerIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState, checkResults]);

  // Recorder lifecycle.
  React.useEffect(() => {
    if (questionState === QuestionStates.SUBMITTED) {
      setError(currentQuestion, undefined);
      wavRecorder?.current?.stop(handleAudioSuccess);

      const timeTaken = round((Date.now() - questionTimeTakenRef.current) / 1000);

      if (assessmentState === AssessmentStates.RUNNING) next();

      question.timeTaken = timeTaken;
      question.submitted = true;
      question.recorded = false;
      question.skipped = false;
      question.visited = true;
      question.analysisRecieved = false;
    }

    if (questionState === QuestionStates.RECORDING) {
      wavRecorder.current = new WavRecorder();
      wavRecorder.current.start();
      questionTimeTakenRef.current = Date.now();

      question.submitted = false;
      question.recorded = true;
      question.skipped = false;
      question.visited = true;
      question.analysisRecieved = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionState, assessmentState]);

  const isPostAssessment = [
    AssessmentStates.WAITING_FOR_RESULT,
    AssessmentStates.FINISHING,
    AssessmentStates.COMPLETED,
  ].includes(assessmentState);

  if (isPostAssessment) {
    return <PostAssessment assessmentState={assessmentState} />;
  }

  // The store ships placeholder defaults (two "Tell something about yourself."
  // questions and a 60-minute totalTime) that only get replaced once the real
  // questions land. Rendering the interview before then shows those fakes as if
  // they were the attempt — and starts TotalTimer's deadline against the wrong
  // total. Hold everything back until the attempt is actually RUNNING.
  const isReady = assessmentState === AssessmentStates.RUNNING;

  return (
    <MobileAppShell
      hideMenu
      footer={
        isReady ? (
          <Box sx={{ px: 4, py: 3 }}>
            <QuestionPills />
          </Box>
        ) : null
      }
    >
      {isReady ? (
        // Fill the viewport (no page scroll) — the question text inside
        // QuestionCard is the only thing that scrolls when it's long.
        <Box
          sx={{
            height: "100%",
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {/* Header card — title, End Session, total countdown */}
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: "shades.clr-white-900",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "neutral.clr-100",
              p: 4,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <AssessmentTitle name={template?.name} />
              <EndSession assessmentState={assessmentState} />
            </Box>

            <TotalTimer time={totalTime} />
          </Box>

          <QuestionCard />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <FullscreenDialog
        open={
          openFullscreenDialog ||
          (!hasIframe &&
            !fullScreenActive &&
            assessmentState !== AssessmentStates.SHOWING_TUTORIAL)
        }
        onSuccess={handleSuccessFullscreenMode}
        remainingTime={
          assessmentState !== AssessmentStates.FULLSCREEN_PERMISSION &&
          assessmentState !== AssessmentStates.SHOWING_TUTORIAL &&
          !fullScreenActive
            ? remainingInterviewTime
            : false
        }
      />
    </MobileAppShell>
  );
}

/**
 * Assessment title — truncated to the first 5 words (+ "…") when the name is
 * longer, and toggles to the full name in place on tap.
 */
function AssessmentTitle({ name = "" }) {
  const [expanded, setExpanded] = React.useState(false);

  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  const isLong = words.length > 5;
  const display = !isLong || expanded ? name : `${words.slice(0, 5).join(" ")}…`;

  return (
    <Typography
      variant="h6-bold"
      color="primary.clr-300"
      onClick={isLong ? () => setExpanded((e) => !e) : undefined}
      sx={{ cursor: isLong ? "pointer" : "default" }}
    >
      {display}
    </Typography>
  );
}

/** Retake action inside the "not recorded properly" snackbar. */
function RetakeButton({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        color: "shades.clr-white-900",
        background: "transparent",
        border: "none",
        borderBottom: "1px solid",
        borderColor: "shades.clr-white-900",
        p: 0,
        cursor: "pointer",
        textTransform: "capitalize",
      }}
    >
      Retake now
    </Box>
  );
}
